import React, { useEffect, useState, useCallback, useRef } from "react";
import "./index.scss";
import {
  assessmentPageImage,
  detailsPageImage,
  listingPageImage,
  onlineInterviewPageImage,
  profileMatchPageImage,
} from "../../Utils/constants";

interface AuthPageSectionProps {
  type: "signin" | "signup";
}

interface Item {
  image: string;
  title: string;
  description: string;
}

type TransitionState = 'entering' | 'visible' | 'exiting' | 'hidden';

const items: Item[] = [
  {
    image: listingPageImage,
    title: "Explore open positions",
    description:
      "Find the right job for you from our extensive job listings",
  },
  {
    image: detailsPageImage,
    title: "Detailed job description",
    description: "View detailed job description and apply for the job",
  },
  {
    image: profileMatchPageImage,
    title: "AI powered profile matching",
    description: "AI will match your resume with the job instantly",
  },
  {
    image: assessmentPageImage,
    title: "AI powered assessments",
    description:
      "Take assessment to test your skills and get matched with the job",
  },
  {
    image: onlineInterviewPageImage,
    title: "Online interviews",
    description: "Easily schedule and attend online interviews with the hiring team",
  },
];

const AuthPageSection = ({ type }: AuthPageSectionProps) => {
  const [currentItem, setCurrentItem] = useState<Item>(items[0]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [transitionState, setTransitionState] = useState<TransitionState>('entering');
  const [isAnimating, setIsAnimating] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const transitionToNextRef = useRef<() => void>();

  const transitionToNext = useCallback(() => {
    if (isAnimating || type === "signin") return;
    
    setIsAnimating(true);
    setCurrentIndex(prevIndex => {
      const nextIndex = (prevIndex + 1) % items.length;
      
      setTransitionState('exiting');
      
      timeoutRef.current = setTimeout(() => {
        setCurrentItem(items[nextIndex]);
        setTransitionState('entering');
        
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setTransitionState('visible');
            setIsAnimating(false);
          });
        });
      }, 600);
      
      return nextIndex;
    });
  }, [isAnimating, type]);

  transitionToNextRef.current = transitionToNext;

  useEffect(() => {
    if (transitionState === 'entering') {
      const timer = setTimeout(() => {
        setTransitionState('visible');
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [transitionState]);

  useEffect(() => {
    if (type === "signin") {
      return;
    }

    intervalRef.current = setInterval(() => {
      transitionToNextRef.current?.();
    }, 4500);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [type]);

  const getTransitionClass = () => {
    switch (transitionState) {
      case 'entering':
        return 'transition-enter';
      case 'visible':
        return 'transition-visible';
      case 'exiting':
        return 'transition-exit';
      case 'hidden':
        return 'transition-hidden';
      default:
        return 'transition-visible';
    }
  };

  const getItem = () => {
    return (
      <div className={`carousel-item ${getTransitionClass()}`}>
        <section
          className={`item-section ${getTransitionClass()}-image`}
          style={{ backgroundImage: `url(${currentItem.image})` }}
        ></section>
        <div className={`item-section-content ${getTransitionClass()}-content`}>
          <h3 className={`${getTransitionClass()}-title`}>{currentItem.title}</h3>
          <p className={`${getTransitionClass()}-description`}>{currentItem.description}</p>
        </div>
      </div>
    );
  };

  return <>{getItem()}</>;
};

export default AuthPageSection;
